import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * SPRZĄTANIE WYSP PO NIEUDANEJ REJESTRACJI.
 *
 * ⚠️ PO CO TO JEST: technik wystawił agenta na dwóch maszynach z uszkodzonym `SPSConfig.XML`,
 * rozpoznanie nie znalazło bazy, a każda próba z kodem ZAŁOŻYCIELSKIM zakłada NOWĄ WYSPĘ.
 * U klienta zostały trzy wyspy w miejsce jednej — a Vending+ pyta o dane wszystkich, bo widzi
 * je na liście. Do 2026-08-19 jedynym wyjściem było wystawienie instancji od nowa.
 */

export const LicznikiWyspySchema = z.object({
  maszyny: z.number(),
  pracownicy: z.number(),
  wydania: z.number(),
  operacje: z.number(),
  wezlyPotwierdzone: z.number(),
  wezlyInne: z.number(),
});

export const WyspaDoSprzataniaSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
  masterDatabase: z.string().nullable(),
  masterServer: z.string().nullable(),
  masterConfirmed: z.boolean(),
  /**
   * WĘZEŁ, NA KTÓRYM STOI MASTER — `null` znaczy „jeszcze nie zasiany".
   *
   * ⚠️ PO TO JEST: dopóki mastera nie ma, nazwę bazy chmurowej wolno WPISAĆ (bo to zapowiedź,
   * nie opis). Po zasianiu nazwa opisuje bazę, w której master naprawdę stoi.
   *
   * ⚠️ `optional`, bo instancja starsza od tej zmiany pola nie odda — a przegląd wysp ma się
   * wtedy narysować, nie wywalić walidacji.
   */
  masterNodeId: z.string().nullable().optional(),
  tryb: z.enum(['ZWYKLY', 'SMART_SYNC']),
  /**
   * Czy wyspa rozsyła wydania rodzeństwa do maszyn (PLAN §35 pkt 8).
   *
   * ⚠️ `optional`, bo instancja starsza od tej zmiany pola nie odda — a ekran ma wtedy pokazać
   * przełącznik jako wyłączony, nie wywalić walidację całego przeglądu wysp.
   */
  wydaniaRodzenstwa: z.boolean().optional(),
  liczniki: LicznikiWyspySchema,
  /**
   * `null` = wolno usunąć. Inaczej `komunikat` mówi ZDANIEM, czego nie wolno ruszyć — ekran
   * pokazuje je zamiast wyszarzonego przycisku bez wyjaśnienia.
   */
  odmowaUsuniecia: z
    .object({ powod: z.string(), komunikat: z.string() })
    .passthrough()
    .nullable(),
});

export type WyspaDoSprzatania = z.infer<typeof WyspaDoSprzataniaSchema>;

export const PrzegladWyspSchema = z.object({ wyspy: z.array(WyspaDoSprzataniaSchema) });

export const fetchPrzegladWysp = () => apiGet('/islands/przeglad', PrzegladWyspSchema);

/**
 * ZDANIE DO PRZEPISANIA PRZY RESECIE INSTANCJI.
 *
 * ⚠️ TA SAMA WARTOŚĆ STOI W API (`api/src/tenancy/kasowanie-wysp.ts`) i pilnuje jej
 * `fraza-resetu.spec.ts`, który CZYTA TAMTEN PLIK. Rozjazd byłby nie do zdiagnozowania z żadnej
 * strony osobno: ekran kazałby przepisać jedno zdanie, a API odrzucałoby je bez wyjaśnienia.
 */
export const FRAZA_RESETU = 'USUN WSZYSTKIE WYSPY';

const WynikUsunieciaSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  usunieteWezly: z.number(),
  usunieteWiersze: z.number(),
  usunieteObiekty: z.number(),
  nieusunieteObiekty: z.number(),
  /** Ile tokenów dostało zlecenie odinstalowania agenta (13.10.2026). `optional` — starsza instancja. */
  zleconeOdinstalowania: z.number().optional(),
});

/**
 * USUNIĘCIE WYSPY RAZEM Z DANYMI — druga trasa, nie flaga przy zwykłym usuwaniu.
 *
 * ⚠️ ZGŁOSZONE (01.09.2026): „czy jest jakaś opcja, żeby wyjebać wyspy do zera". Zwykłe usuwanie
 * przepuszcza wyłącznie wyspę-widmo i takie ma zostać — tutaj potwierdzeniem jest PRZEPISANY KOD
 * WYSPY, bo znikają dane, których nikt nie odtworzy.
 */
export const usunWyspeZDanymi = (id: string, potwierdzenie: string, odinstalujAgentow: boolean) =>
  apiSend('POST', `/islands/${id}/usun-z-danymi`, { potwierdzenie, odinstalujAgentow }, WynikUsunieciaSchema);

/** WYCZYSZCZENIE CAŁEJ INSTANCJI — wszystkie wyspy naraz, po przepisaniu frazy. */
export const resetInstancji = (potwierdzenie: string, odinstalujAgentow: boolean) =>
  apiSend(
    'POST',
    '/islands/reset',
    { potwierdzenie, odinstalujAgentow },
    z.object({
      wysp: z.number(),
      wyspy: z.array(z.object({ code: z.string(), name: z.string() })),
      wezlow: z.number(),
      wierszy: z.number(),
      kodowRejestracyjnych: z.number(),
      zleconeOdinstalowania: z.number().optional(),
      usunieteObiekty: z.number(),
      nieusunieteObiekty: z.number(),
    }),
  );

export const usunWyspe = (id: string) =>
  apiSend(
    'DELETE',
    `/islands/${id}`,
    undefined,
    z.object({ id: z.string(), code: z.string(), name: z.string(), usunieta: z.boolean(), usunieteWezly: z.number() }),
  );
